import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import {
  API_GOOGLE_LOCATIONS,
  API_RESOLVE_FAILING_SN
} from '../utils/constants';
import { useSelector } from 'react-redux';
import { authSelector } from '../store/slices/auth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const axios = require('axios');

const NewGoogleConnection = ({
  open,
  onOpenChange,
  currentClientId,
  currentClient,
  connectSnCallback
}) => {
  const { isAuthenticated, userInfo } = useSelector(authSelector);
  const [state, setState] = useState({
    optionsData: null,
    loaded: false,
    googleLocation: null,
    clientId: null,
    savingClient: false
  });
  const [isFailing, setIsFailing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleResolved = async (snName) => {
    let config = {
      method: 'post',
      url: API_RESOLVE_FAILING_SN,
      data: { clientId: currentClientId, snName },
      headers: { 'Content-Type': 'application/json' }
    };

    if (isFailing) {
      setIsLoading(true);
      await axios(config)
        .then(async (response) => {
          if (response.status === 200) {
            setIsFailing(false);
            setIsLoading(false);
            await someFn();
          }
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (currentClient?.clientSocialNetworks?.gmbFailing !== undefined) {
      setIsFailing(currentClient?.clientSocialNetworks?.gmbFailing);
    } else {
      setIsFailing(false);
    }
  }, [currentClient, currentClientId]);

  useEffect(() => {
    getLocations();
    // eslint-disable-next-line
  }, []);

  function getLocations() {
    axios
      .get(API_GOOGLE_LOCATIONS, {
        headers: {
          Authorization: `bearer ${userInfo.accessToken}`
        }
      })
      .then(async (response) => {
        const pagesData = [];
        await response.data.pages.forEach((loct) => {
          pagesData.push(loct);
        });
        setState((prev) => ({ ...prev, optionsData: pagesData, loaded: true }));
      })
      .catch((error) => {
        console.error('error ' + error);
      });
  }

  const handleSelectChanges = (option) => {
    if (option) {
      const options = { id: option.value, locationName: option.label };
      setState((prev) => ({
        ...prev,
        googleLocation: options,
        clientId: currentClientId
      }));
    }
  };

  const googlePageConnectionHandler = () => {
    setState((prev) => ({ ...prev, savingClient: true }));
    if (state.googleLocation === undefined || state.googleLocation === null) {
      alert('Please select a location.');
      setState((prev) => ({ ...prev, savingClient: false }));
      return;
    }

    const bodyPut = {
      clientId: state.clientId,
      googleLocationId: state.googleLocation.id,
      googleLocationName: state.googleLocation.locationName
    };

    const url = API_GOOGLE_LOCATIONS;

    axios
      .put(url, bodyPut, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `bearer ${userInfo.accessToken}`
        }
      })
      .then(async function (response) {
        if (response.status === 200) {
          setState((prev) => ({ ...prev, savingClient: false }));
          await someFn();
          onOpenChange(false);
        }
      })
      .catch(function (response) {
        setState((prev) => ({ ...prev, savingClient: false }));
      });
  };

  const someFn = async () => {
    await connectSnCallback();
  };

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      borderRadius: '0.5rem',
      borderColor: state.isFocused
        ? 'hsl(var(--primary))'
        : 'hsl(var(--border))',
      boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--primary))' : 'none',
      '&:hover': {
        borderColor: 'hsl(var(--primary))'
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      zIndex: 50
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? 'hsl(var(--primary))'
        : state.isFocused
        ? 'hsl(var(--accent))'
        : 'transparent',
      cursor: 'pointer'
    })
  };

  const { optionsData } = state;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Connect Google Business Profile
          </DialogTitle>
          <DialogDescription>
            Select the Google Business location for{' '}
            {currentClient?.clientName || 'this client'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Google Location Selector */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">
                Google Business Location
              </Label>
              <span className="text-sm text-muted-foreground">
                {optionsData?.length || 0} locations available
              </span>
            </div>
            <Select
              options={optionsData?.map((option) => ({
                value: option.id,
                label: option.locationName
              }))}
              onChange={handleSelectChanges}
              isSearchable
              isClearable
              placeholder="Search for a business location..."
              isLoading={!optionsData}
              styles={customSelectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
            <p className="text-xs text-muted-foreground">
              Select the Google My Business location you want to connect
            </p>
          </div>

          {/* Failing Status Alert */}
          {isAuthenticated &&
            userInfo.role === 'superAdmin' &&
            isFailing &&
            currentClient?.clientSocialNetworks?.googleMyBusiness && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium mb-1">
                      This Google connection is failing
                    </p>
                    <p className="text-xs">
                      Currently connected location may have errors
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      htmlFor="resolve-google-checkbox"
                      className="text-xs cursor-pointer">
                      Mark as resolved
                    </label>
                    <input
                      id="resolve-google-checkbox"
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer"
                      onChange={() => handleResolved('googleMyBusiness')}
                      checked={!isFailing}
                      disabled={isLoading}
                    />
                    {isLoading && (
                      <Loader2 className="h-4 w-4 animate-spin ml-1" />
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

          {/* Success indicator when location is selected */}
          {state.googleLocation && (
            <Alert>
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Selected: <strong>{state.googleLocation.locationName}</strong>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={state.savingClient}>
            Cancel
          </Button>
          <Button
            onClick={googlePageConnectionHandler}
            disabled={state.savingClient || !state.googleLocation}
            className="min-w-[120px]">
            {state.savingClient ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Location'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewGoogleConnection;
